<template>
    <div v-if="form" class="mt-3">
        <div class="alert alert-info py-2 small">
            <i class="bi bi-info-circle me-1"></i>
            Actividad de contenido: no puntúa. Se marca como completada en cuanto el jugador entra en ella.
        </div>
        <div class="mb-3">
            <label for="formControlTitle" class="form-label">Título:</label>
            <input type="text" :class="'form-control'+((error_title != false) ? ' border-danger' : '')" id="formControlTitle" placeholder="Título de la actividad" v-model="form_data.title"/>
            <div class="w-100 d-flex justify-content-between">
                <div>
                    <span v-if="error_title != false" class="text-danger">{{ error_title }}</span>
                </div>
                <span class="text-primary">{{ (255 - (form_data.title || '').length) }}</span>
            </div>
        </div>

        <!-- Image / video / PDF: an external URL or an uploaded file -->
        <div class="mb-3" v-if="content_type != utils.ACTIVITY_TYPE.HTML">
            <label for="formControlMediaUrl" class="form-label">{{ media_label }}:</label>
            <small class="text-secondary ms-2">{{ media_hint }}</small>
            <div class="input-group">
                <input type="text" :class="'form-control'+((error_media != false) ? ' border-danger' : '')" id="formControlMediaUrl" placeholder="https://..." v-model="form_data.media_url"/>
                <label class="btn btn-outline-primary mb-0" :class="{disabled: uploading}">
                    <i class="bi bi-upload me-1"></i>Subir archivo
                    <input type="file" class="d-none" :accept="media_accept" @change="upload_file" :disabled="uploading">
                </label>
            </div>
            <small v-if="error_media != false" class="text-danger">{{ error_media }}</small>

            <!-- Runs in the background: the rest of the form stays usable
                 while it uploads, only "Guardar" waits for it. -->
            <div v-if="uploading" class="d-flex align-items-center mt-2">
                <div class="progress flex-fill" role="progressbar" :aria-valuenow="upload_percent" aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{width: upload_percent + '%'}">{{ upload_percent }}%</div>
                </div>
                <small class="text-secondary ms-2 text-nowrap">{{ format_mb(upload_sent) }} / {{ format_mb(upload_total) }} MB</small>
                <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="cancel_upload"><i class="bi bi-x-lg"></i></button>
            </div>

            <div class="content-preview mt-3" v-if="form_data.media_url">
                <img v-if="content_type == utils.ACTIVITY_TYPE.IMAGE" :src="form_data.media_url" class="img-fluid">
                <template v-if="content_type == utils.ACTIVITY_TYPE.VIDEO && video_source">
                    <iframe v-if="video_source.kind != 'file'" :src="video_source.src" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>
                    <video v-else :src="video_source.src" controls></video>
                </template>
                <iframe v-if="content_type == utils.ACTIVITY_TYPE.PDF" :src="form_data.media_url"></iframe>
                <PdfSlides v-if="slides" :url="form_data.media_url" />
            </div>
            <div v-if="slides && form_data.media_url" class="small mt-1">
                <span v-if="counting_pages" class="text-secondary"><span class="spinner-border spinner-border-sm me-1"></span>Contando diapositivas...</span>
                <span v-else-if="form_data.page_count" class="text-success"><i class="bi bi-check-circle me-1"></i>{{ form_data.page_count }} diapositivas</span>
                <span v-else class="text-danger"><i class="bi bi-exclamation-triangle me-1"></i>No se ha podido leer el PDF. Si es de otra web, descárgalo y súbelo como archivo.</span>
            </div>
        </div>

        <!-- Event mode only: the room's projector can be the one place it plays. -->
        <div class="mb-3" v-if="content_type == utils.ACTIVITY_TYPE.VIDEO && mode != utils.APP_MODES.ONLINE">
            <label class="form-label">Dónde se ve:</label>
            <div class="form-check">
                <input class="form-check-input" type="radio" id="displayOnAll" :value="utils.MEDIA_DISPLAY.ALL" v-model="form_data.display_on">
                <label class="form-check-label" for="displayOnAll">En los dispositivos de todos los equipos</label>
            </div>
            <div class="form-check">
                <input class="form-check-input" type="radio" id="displayOnScreen" :value="utils.MEDIA_DISPLAY.SCREEN" v-model="form_data.display_on">
                <label class="form-check-label" for="displayOnScreen">Solo en la pantalla grande de la sala <small class="text-secondary">(los móviles muestran "Mira la pantalla grande")</small></label>
            </div>
        </div>

        <div class="mb-3" v-if="content_type != utils.ACTIVITY_TYPE.HTML">
            <label for="formControlDescription" class="form-label">Texto complementario:</label> <small class="text-secondary">Opcional, se muestra debajo del contenido.</small>
            <textarea class="form-control" id="formControlDescription" rows="2" v-model="form_data.description"></textarea>
        </div>

        <!-- Free HTML: WYSIWYG editor, with a raw-source escape hatch -->
        <div class="mb-3" v-if="content_type == utils.ACTIVITY_TYPE.HTML">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label mb-0">Contenido:</label>
                <div class="btn-group btn-group-sm">
                    <button type="button" :class="'btn ' + (form_data.html_mode != 'code' ? 'btn-primary' : 'btn-outline-primary')" @click="set_html_mode('visual')"><i class="bi bi-type me-1"></i>Visual</button>
                    <button type="button" :class="'btn ' + (form_data.html_mode == 'code' ? 'btn-primary' : 'btn-outline-primary')" @click="set_html_mode('code')"><i class="bi bi-code-slash me-1"></i>Código HTML</button>
                </div>
            </div>
            <div v-show="form_data.html_mode != 'code'" :class="'html-editor' + ((error_html != false) ? ' border border-danger' : '')">
                <div ref="editor"></div>
            </div>
            <textarea v-if="form_data.html_mode == 'code'" :class="'form-control font-monospace'+((error_html != false) ? ' border-danger' : '')" rows="14" v-model="form_data.html" placeholder="<h2>Hola</h2>"></textarea>
            <small v-if="error_html != false" class="text-danger">{{ error_html }}</small>
        </div>

        <div class="alert alert-secondary py-2 small" v-if="slides && mode != utils.APP_MODES.ONLINE">
            <i class="bi bi-easel me-1"></i>
            Se muestra una diapositiva cada vez, la misma en todos los dispositivos. Cada "Siguiente" (en el panel o con un mando de presentaciones en la pantalla del manager) pasa a la siguiente diapositiva; en la última, pasa a la siguiente actividad. "Anterior" vuelve atrás.
        </div>

        <!-- A PDF is a document to read at your own pace. Slides to go
             through one by one are a different type - easy to mix up. -->
        <div class="alert alert-warning py-2 small d-flex align-items-center gap-2" v-if="content_type == utils.ACTIVITY_TYPE.PDF">
            <i class="bi bi-easel"></i>
            <div class="flex-fill">¿Son diapositivas para ir pasando una a una? Usa el tipo <b>Presentación</b>: se ven de una en una en 16:9 y "Siguiente" va pasándolas.</div>
            <button type="button" class="btn btn-sm btn-warning text-nowrap" @click="convert_to_presentation"><i class="bi bi-arrow-repeat me-1"></i>Convertir en presentación</button>
        </div>

        <div class="mb-3" v-if="mode != utils.APP_MODES.ONLINE && !slides && content_type != utils.ACTIVITY_TYPE.PDF">
            <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="formControlManualAdvance" v-model="form_data.manual_advance">
                <label class="form-check-label" for="formControlManualAdvance">Avance manual (lo marca el presentador)</label>
            </div>
            <small class="text-secondary d-block">
                Sin cuenta atrás: sigue en pantalla hasta que el presentador avance, desde el panel o con un mando de presentaciones conectado a la pantalla del manager.
            </small>
        </div>

        <div class="mb-3" v-if="mode != utils.APP_MODES.ONLINE">
            <div class="row">
                <div class="col" v-if="!manual">
                    <label for="formControlDuration" class="form-label">Duración:</label> <small class="text-secondary">Segundos que el contenido está en pantalla.</small>
                    <input type="number" :class="'form-control'+((error_duration != false) ? ' border-danger' : '')" id="formControlDuration" placeholder="Duración de la actividad" v-model="form_data.duration"/>
                    <small v-if="error_duration != false" class="text-danger">{{ error_duration }}</small>
                </div>
                <div class="col">
                    <label for="formControlTitleDuration" class="form-label">Duración título:</label> <small class="text-secondary">Segundos que se ve el título antes de mostrar el contenido (reproducción automática de etapa).</small>
                    <input type="number" class="form-control" id="formControlTitleDuration" placeholder="Duración del título" v-model="form_data.title_duration"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import api from '@/api'
import { countPdfPages } from '@/utils/pdf'
import PdfSlides from '@/components/PdfSlides.vue'

const TOOLBAR = [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image', 'video', 'blockquote'],
    ['clean'],
];

export default {
    components: { PdfSlides },
    props: {
        form: {
            type: Object,
            default: null,
            required: true
        },
        // Panel.vue's "new activity" flow picks the type before the form
        // exists, so it's passed in; when editing it's already on form.type.
        type: {
            type: Number,
            default: null
        }
    },
    data(){
        return {
            utils: require('../../../../shared/utils'),
            mode: window.MODE,
            form_data: {
                title: ""
            },
            quill: null,
            uploading: false,
            upload_sent: 0,
            upload_total: 0,
            upload_controller: null,
            error_title: false,
            error_media: false,
            error_html: false,
            error_duration: false,
            counting_pages: false,
        };
    },
    computed: {
        content_type(){
            return this.form_data.type || this.type || this.form.type;
        },
        media_label(){
            switch(this.content_type){
                case this.utils.ACTIVITY_TYPE.IMAGE: return "Imagen";
                case this.utils.ACTIVITY_TYPE.VIDEO: return "Vídeo";
                case this.utils.ACTIVITY_TYPE.PDF: return "Documento PDF";
                case this.utils.ACTIVITY_TYPE.PRESENTATION: return "Presentación (PDF)";
            }
            return "Archivo";
        },
        media_hint(){
            switch(this.content_type){
                case this.utils.ACTIVITY_TYPE.IMAGE: return "URL de la imagen o sube un archivo (jpg, png, gif, webp).";
                case this.utils.ACTIVITY_TYPE.VIDEO: return "Enlace de YouTube/Vimeo, URL directa a un .mp4 o sube un archivo.";
                case this.utils.ACTIVITY_TYPE.PDF: return "URL del PDF o sube un archivo.";
                case this.utils.ACTIVITY_TYPE.PRESENTATION: return "Sube las diapositivas exportadas a PDF (PowerPoint: Archivo → Exportar → PDF). Mejor en 16:9.";
            }
            return "";
        },
        media_accept(){
            switch(this.content_type){
                case this.utils.ACTIVITY_TYPE.IMAGE: return "image/jpeg,image/png,image/gif,image/webp";
                case this.utils.ACTIVITY_TYPE.VIDEO: return "video/mp4,video/webm,video/ogg,video/quicktime";
                case this.utils.ACTIVITY_TYPE.PDF:
                case this.utils.ACTIVITY_TYPE.PRESENTATION: return "application/pdf";
            }
            return "";
        },
        video_source(){
            return this.utils.get_video_source(this.form_data.media_url);
        },
        // A presentation: the server needs its page count to know when
        // "Siguiente" should leave the activity instead of turning the page.
        slides(){
            return this.utils.is_presenter_slides({type: this.content_type});
        },
        // No clock in event mode - no duration to ask for.
        manual(){
            return this.utils.is_manual_advance({...this.form_data, type: this.content_type});
        },
        upload_percent(){
            return this.upload_total > 0 ? Math.floor(this.upload_sent * 100 / this.upload_total) : 0;
        }
    },
    watch: {
        'form_data.title'(){
            this.error_title = false;
        },
        'form_data.media_url'(){
            this.error_media = false;
            this.count_pages();
        },
        slides(){
            this.count_pages();
        },
        'form_data.html'(){
            this.error_html = false;
        },
    },
    mounted(){
        this.form.validate = this.validate;
        if(this.form.id !== undefined) {
            this.form_data = Object.assign({}, this.form);
            if(this.form_data.title_duration === undefined) this.form_data.title_duration = 5;
            if(this.form_data.display_on === undefined) this.form_data.display_on = this.utils.MEDIA_DISPLAY.ALL;
        } else {
            this.form_data.type = this.type;
            this.form_data.media_url = "";
            this.form_data.description = "";
            this.form_data.html = "";
            this.form_data.html_mode = "visual";
            this.form_data.choices = [];
            this.form_data.correctAnswer = [];
            this.form_data.duration = 60;
            this.form_data.display_on = this.utils.MEDIA_DISPLAY.ALL;
            this.form_data.score = 0;
            this.form_data.title_duration = 5;
            this.form_data.result_duration = 0;
            this.form_data.manual_advance = false;
            this.form_data.stage_id = this.form.stage_id;
        }

        if(this.content_type == this.utils.ACTIVITY_TYPE.HTML)
            this.$nextTick(this.init_editor);
    },
    beforeUnmount(){
        this.quill = null;
        clearTimeout(this.count_timer);
        // Closing the modal mid-upload: nobody's left to receive the URL.
        this.cancel_upload();
    },
    methods: {
        init_editor(){
            if(!this.$refs.editor) return;
            this.quill = new Quill(this.$refs.editor, {
                theme: 'snow',
                placeholder: 'Escribe aquí el contenido que verán los jugadores...',
                modules: { toolbar: TOOLBAR }
            });
            if(this.form_data.html)
                this.quill.clipboard.dangerouslyPasteHTML(this.form_data.html, 'silent');
            this.quill.on('text-change', () => {
                this.form_data.html = this.quill.getLength() > 1 ? this.quill.getSemanticHTML() : "";
            });
        },
        // Going code -> visual re-parses the HTML through Quill, which only
        // keeps what its own formats understand (no custom styles/tables/
        // scripts). Raw HTML written in "code" mode is saved untouched as
        // long as the admin doesn't switch back.
        set_html_mode(mode){
            if(mode == this.form_data.html_mode) return;
            if(mode == 'visual' && this.quill){
                if(this.form_data.html && !window.confirm("El editor visual puede simplificar el HTML escrito a mano (estilos, tablas...). ¿Continuar?"))
                    return;
                this.quill.setContents([], 'silent');
                if(this.form_data.html)
                    this.quill.clipboard.dangerouslyPasteHTML(this.form_data.html, 'silent');
            }
            this.form_data.html_mode = mode;
        },
        async upload_file(event){
            const file = event.target.files[0];
            event.target.value = "";
            if(!file) return;

            this.uploading = true;
            this.upload_sent = 0;
            this.upload_total = file.size;
            const controller = new AbortController();
            this.upload_controller = controller;
            try{
                const url = await api.upload_media(file, this.$store.state.game.session, {
                    signal: controller.signal,
                    onProgress: (sent) => { this.upload_sent = sent; },
                });
                this.form_data.media_url = url;
            }catch(ex){
                if(!controller.signal.aborted)
                    window.toast.error(ex.message);
            }finally{
                if(this.upload_controller === controller){
                    this.uploading = false;
                    this.upload_controller = null;
                }
            }
        },
        // Keeps the file and everything else - only how it's shown changes
        // (and it gets no clock: a presentation is always presenter-driven).
        convert_to_presentation(){
            this.form_data.type = this.utils.ACTIVITY_TYPE.PRESENTATION;
            this.form_data.manual_advance = false;
        },
        async count_pages(){
            const url = (this.form_data.media_url || "").trim();
            clearTimeout(this.count_timer);
            if(!this.slides || !url){
                this.counting_pages = false;
                return;
            }
            this.counting_pages = true;
            // Debounced: media_url changes on every keystroke when typed.
            await new Promise((resolve) => { this.count_timer = setTimeout(resolve, 400); });
            if(url != (this.form_data.media_url || "").trim()) return;
            const pages = await countPdfPages(url);
            if(url != (this.form_data.media_url || "").trim()) return;
            this.form_data.page_count = pages;
            this.counting_pages = false;
        },
        cancel_upload(){
            if(!this.upload_controller) return;
            this.upload_controller.abort();
            this.upload_controller = null;
            this.uploading = false;
        },
        format_mb(bytes){
            return (bytes / 1024 / 1024).toFixed(1);
        },
        validate(){
            this.error_title = false;
            this.error_media = false;
            this.error_html = false;
            this.error_duration = false;

            if(!this.form_data.title)
                this.error_title = "Campo obligatorio!";
            else if(this.form_data.title.length > 255)
                this.error_title = "El titulo de la actividad es demasiado largo!";

            if(this.content_type == this.utils.ACTIVITY_TYPE.HTML){
                if(!this.form_data.html || !this.form_data.html.trim())
                    this.error_html = "Campo obligatorio!";
            }else if(!this.form_data.media_url || !this.form_data.media_url.trim()){
                this.error_media = "Campo obligatorio!";
            }

            if(this.mode != this.utils.APP_MODES.ONLINE && !this.manual && !(this.form_data.duration > 0))
                this.error_duration = "Campo obligatorio!";

            if(this.uploading)
                this.error_media = "Espera a que termine la subida del archivo.";
            else if(this.slides && this.counting_pages)
                this.error_media = "Espera a que termine de leerse el PDF.";
            else if(this.slides && this.form_data.media_url && !this.form_data.page_count)
                this.error_media = "No se ha podido leer el PDF de la presentación.";

            if(this.uploading || (this.slides && this.counting_pages) || this.error_title || this.error_media || this.error_html || this.error_duration)
                return false;

            window.network.wso.send(JSON.stringify({action: (this.form.id !== undefined) ? window.RPC.EDIT_ACTIVITY : window.RPC.REGISTER_ACTIVITY, params: this.form_data}));
            return true;
        }
    }
}
</script>

<style scoped>
    .content-preview{
        max-height: 320px;
        overflow: hidden;
        border: 1px solid var(--vc-line, #dee2e6);
        border-radius: 8px;
        background-color: #f8f9fa;
        display: flex;
        justify-content: center;
    }
    .content-preview :deep(.pdf-slides){
        width: min(100%, calc(320px * 16 / 9));
    }
    .content-preview img{
        max-height: 320px;
        object-fit: contain;
    }
    .content-preview iframe,
    .content-preview video{
        width: 100%;
        height: 320px;
        border: 0;
    }
    .html-editor :deep(.ql-container){
        min-height: 260px;
        font-size: 1rem;
    }
</style>
